import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  SHOWREEL_URL,
  SHOWREEL_MOBILE_URL,
  SHOWREEL_POSTER_URL,
  SHOWREEL_MOBILE_POSTER_URL,
} from '../data/portfolioData';

const STUDIO_INDEX = [
  {
    num: '01',
    title: 'Brand Work & Micro-Dramas',
    meta: 'BKIC · Alcobrew BHRUM · Seef · XPENG · DramaBox · Dashverse',
    mode: '100% AI & Hybrid',
    href: '#featured-work',
  },
  {
    num: '02',
    title: 'Feature Film VFX (Telugu Cinema)',
    meta: '#CULT (Vishwak Sen) · HONEY (Naveen Chandra) · MANODU G.O.A.T',
    mode: 'Hybrid Live-Action VFX',
    href: '#feature-films',
  },
  {
    num: '03',
    title: 'AI UGC, Product & Spec Ads',
    meta: 'Vendoz · Zoom Shoes · Fine Jewellery · Sony · Haute Couture · Fevi Stick',
    mode: '100% AI-Native',
    href: '#featured-work',
  },
  {
    num: '04',
    title: 'MotionX Originals & Music Videos',
    meta: 'Ashes of Fire ★ · Jatayu · Flamingos · Mental · Aasmaan · Run',
    mode: 'Original IP',
    href: '#originals',
  },
];

const HeroSection = () => {
  const [isMobileViewport, setIsMobileViewport] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth < 768 : false
  );
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isMuted, setIsMuted] = useState(true);
  const [showreelModalOpen, setShowreelModalOpen] = useState(false);

  const trackRef = useRef(null);
  const heroVideoRef = useRef(null);

  useEffect(() => {
    const onResize = () => {
      setIsMobileViewport(window.innerWidth < 768);
    };
    window.addEventListener('resize', onResize, { passive: true });
    onResize();
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const el = trackRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const totalScrollable = Math.max(1, el.offsetHeight - window.innerHeight);
      const scrolled = Math.min(totalScrollable, Math.max(0, -rect.top));
      setScrollProgress(scrolled / totalScrollable);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const activeVideoSrc = isMobileViewport ? SHOWREEL_MOBILE_URL : SHOWREEL_URL;
  const activePosterSrc = isMobileViewport
    ? SHOWREEL_MOBILE_POSTER_URL
    : SHOWREEL_POSTER_URL;

  const handleLoadedMetadata = (e) => {
    try {
      const v = e.currentTarget;
      if (v.currentTime < 2.0) {
        v.currentTime = isMobileViewport ? 4.0 : 14.0;
      }
    } catch {}
  };

  const toggleHeroSound = () => {
    const v = heroVideoRef.current;
    if (!v) return;
    v.muted = !isMuted;
    setIsMuted(!isMuted);
  };

  const scrollToSection = (e, href) => {
    e.preventDefault();
    const id = href.replace('#', '');
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  // Smooth interpolation for Scroll-Driven Portal Expansion
  const expandT = Math.min(1, scrollProgress / 0.8);
  const startWidthVw = isMobileViewport ? 92 : 86;
  const startHeightVh = isMobileViewport ? 52 : 62;
  const currentWidthVw = startWidthVw + (100 - startWidthVw) * expandT;
  const currentHeightVh = startHeightVh + (100 - startHeightVh) * expandT;
  const currentMarginTopPx = Math.round((isMobileViewport ? 140 : 148) * (1 - expandT));
  const currentRadiusPx = Math.round(18 * (1 - expandT));
  const headerOpacity = Math.max(0, 1 - expandT * 1.65);
  const headerTranslateY = -Math.round(expandT * 44);
  const overlayDockOpacity = expandT > 0.45 ? Math.min(1, (expandT - 0.45) / 0.45) : 0;

  return (
    <>
      <section className="relative bg-black">
        {/* Tall Scroll Track for Scroll-Driven Expand-to-Cinema Portal */}
        <div
          ref={trackRef}
          className="relative w-full h-[185vh] md:h-[215vh] bg-black"
        >
          <div className="sticky top-0 h-screen w-full overflow-hidden flex items-center justify-center">
            {/* Split Architectural Header — Aligned Flush with the Left & Right Edges of the Cinema Portal */}
            <div
              className="absolute top-[68px] sm:top-[78px] z-20 mx-auto flex flex-col lg:flex-row lg:items-end justify-between gap-3 lg:gap-10 pb-3 border-b border-white/[0.09]"
              style={{
                width: `${startWidthVw}vw`,
                opacity: headerOpacity,
                transform: `translate3d(0, ${headerTranslateY}px, 0)`,
                pointerEvents: headerOpacity < 0.15 ? 'none' : 'auto',
              }}
            >
              {/* Left Column: Engine Tag + Editorial Title */}
              <div>
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="w-2 h-2 rounded-full bg-[#E50914] shadow-[0_0_10px_#E50914]" />
                  <span className="text-[10px] sm:text-[11px] font-mono uppercase tracking-[0.15em] text-[#E50914] font-medium">
                    100% AI-Native Production &nbsp;×&nbsp; Hybrid Live-Action + AI VFX
                  </span>
                </div>

                <h1 className="text-white font-normal tracking-[-0.035em] leading-[1.02] text-[clamp(1.65rem,3.3vw,3.45rem)]">
                  Impossible worlds.{' '}
                  <span className="text-[#E50914]">Real emotion.</span>
                </h1>
              </div>

              {/* Right Column: Concise Studio Descriptor aligned to right edge of portal */}
              <div className="max-w-md lg:text-right">
                <p className="text-[#a1a7b3] text-[clamp(0.78rem,0.95vw,0.95rem)] leading-[1.45]">
                  Commercials, <span className="text-white">9:16 micro-dramas</span>, theatrical{' '}
                  <span className="text-white">feature film VFX</span>, and original cinema —
                  delivered <span className="text-[#E50914] font-medium">3–5× faster</span> with
                  100% client-owned IP.
                </p>
              </div>
            </div>

            {/* Scroll-Driven Expanding Cinema Portal */}
            <div
              className="relative overflow-hidden bg-[#09090b] shadow-[0_0_70px_rgba(229,9,20,0.08)] flex items-end justify-between"
              style={{
                width: `${currentWidthVw}vw`,
                height: `${currentHeightVh}vh`,
                marginTop: `${currentMarginTopPx}px`,
                borderRadius: `${currentRadiusPx}px`,
                border:
                  expandT < 0.95
                    ? '1px solid rgba(255,255,255,0.14)'
                    : '0px solid transparent',
              }}
            >
              <video
                ref={heroVideoRef}
                key={activeVideoSrc}
                src={activeVideoSrc}
                poster={activePosterSrc}
                onLoadedMetadata={handleLoadedMetadata}
                autoPlay
                loop
                muted={isMuted}
                playsInline
                className="absolute inset-0 w-full h-full object-cover"
                style={{ filter: 'brightness(0.92) contrast(1.04)' }}
              />

              {/* Subtle Bottom Vignette */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 via-[35%] to-transparent pointer-events-none" />

              {/* Corner Controls Inside Framed Portal (Before Scroll Expansion) */}
              <div
                className="absolute inset-x-0 bottom-4 sm:bottom-6 z-20 px-4 sm:px-7 flex flex-wrap items-center justify-between gap-3 transition-opacity duration-200"
                style={{
                  opacity: 1 - overlayDockOpacity,
                  pointerEvents: overlayDockOpacity > 0.5 ? 'none' : 'auto',
                }}
              >
                <div className="flex items-center gap-2.5 bg-black/70 backdrop-blur-md px-3.5 py-1.5 rounded-full border border-white/15">
                  <span className="w-2 h-2 rounded-full bg-[#E50914] animate-pulse" />
                  <span className="text-[11px] font-mono uppercase tracking-[0.1em] text-white/90">
                    {isMobileViewport ? 'Mobile Reel · Clean Cut' : '2026 Studio Showreel'}
                  </span>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={toggleHeroSound}
                    className="bg-black/70 hover:bg-white hover:text-black backdrop-blur-md border border-white/20 text-white px-3.5 py-1.5 rounded-full text-[11px] sm:text-[12px] transition-colors cursor-pointer"
                  >
                    {isMuted ? '🔇 Unmute' : '🔊 Sound On'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowreelModalOpen(true)}
                    className="bg-[#E50914] hover:bg-[#ff1a25] text-white px-4 py-1.5 rounded-full text-[11px] sm:text-[12px] font-medium transition-colors shadow-[0_0_20px_rgba(229,9,20,0.4)] cursor-pointer"
                  >
                    ▶ Full Screen
                  </button>
                </div>
              </div>

              {/* Expanded Full-Viewport Cinema Dock (Appears as portal reaches 100vw x 100vh) */}
              <div
                className="relative z-20 w-full px-5 sm:px-10 lg:px-16 xl:px-24 pb-8 sm:pb-12 flex flex-col lg:flex-row lg:items-end justify-between gap-6 transition-opacity duration-200"
                style={{
                  opacity: overlayDockOpacity,
                  pointerEvents: overlayDockOpacity < 0.3 ? 'none' : 'auto',
                }}
              >
                <div className="max-w-2xl">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="w-2 h-2 rounded-full bg-[#E50914]" />
                    <p className="text-[10px] sm:text-[11px] font-mono uppercase tracking-[0.14em] text-[#E50914] font-medium">
                      MotionX Showreel &nbsp;·&nbsp; Dual-Engine Cinema
                    </p>
                  </div>
                  <h2 className="text-white font-normal tracking-[-0.03em] leading-[1.04] text-[clamp(1.6rem,3.2vw,3.25rem)]">
                    Real actors &amp; Hybrid VFX. Or{' '}
                    <span className="text-[#E50914]">100% AI-generated</span> from scratch.
                  </h2>
                </div>

                <div className="flex flex-wrap items-center gap-2.5 shrink-0">
                  <button
                    type="button"
                    onClick={() => setShowreelModalOpen(true)}
                    className="inline-flex items-center gap-2 bg-[#E50914] hover:bg-[#ff1a25] text-white px-5 py-2.5 sm:py-3 rounded-full text-[12px] sm:text-[13px] font-medium transition-all shadow-[0_0_24px_rgba(229,9,20,0.4)] cursor-pointer"
                  >
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor">
                      <polygon points="5,3 20,12 5,21" />
                    </svg>
                    Watch Full Showreel
                  </button>

                  <button
                    type="button"
                    onClick={toggleHeroSound}
                    className="inline-flex items-center gap-2 bg-black/65 hover:bg-white hover:text-black backdrop-blur-md border border-white/20 text-white px-4 py-2.5 sm:py-3 rounded-full text-[12px] sm:text-[13px] transition-all cursor-pointer"
                  >
                    {isMuted ? '🔇 Unmute Reel' : '🔊 Sound On'}
                  </button>

                  <a
                    href="#featured-work"
                    onClick={(e) => scrollToSection(e, '#featured-work')}
                    className="inline-flex items-center gap-2 bg-white text-black hover:bg-white/90 px-4 py-2.5 sm:py-3 rounded-full text-[12px] sm:text-[13px] font-medium transition-all no-underline"
                  >
                    Explore Work ↓
                  </a>
                </div>
              </div>
            </div>

            {/* Scroll Indicator at bottom of initial stage */}
            <div
              className="absolute bottom-2.5 inset-x-0 flex items-center justify-center gap-2 text-[11px] font-mono uppercase tracking-[0.16em] text-white/60 pointer-events-none transition-opacity duration-150"
              style={{ opacity: Math.max(0, 1 - expandT * 2.2) }}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-[#E50914]" />
              <span>Scroll to Expand Showreel ↓</span>
            </div>
          </div>
        </div>

        {/* 4-Column Responsive Studio Index Bar with Cinema Red Accents */}
        <div className="w-full border-y border-white/[0.09] bg-[#050505]">
          <div className="w-full px-5 sm:px-10 lg:px-16 xl:px-24 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 divide-y sm:divide-y-0 sm:divide-x divide-white/[0.08]">
            {STUDIO_INDEX.map((col) => (
              <a
                key={col.num}
                href={col.href}
                onClick={(e) => scrollToSection(e, col.href)}
                className="group py-5 sm:py-6 sm:px-6 first:sm:pl-0 last:sm:pr-0 flex flex-col justify-between hover:bg-white/[0.02] transition-colors no-underline"
              >
                <div className="flex items-center justify-between gap-2 mb-2">
                  <span className="text-[11px] font-mono uppercase tracking-[0.12em] text-[#E50914] font-semibold">
                    {col.num}
                  </span>
                  <span className="text-[11px] uppercase tracking-[0.08em] text-white/50 group-hover:text-[#E50914] transition-colors">
                    {col.mode} →
                  </span>
                </div>
                <h2 className="text-[clamp(0.95rem,1.1vw,1.1rem)] font-medium tracking-[-0.02em] text-white group-hover:text-white/90">
                  {col.title}
                </h2>
                <p className="text-[12px] text-[#767d88] leading-[1.45] mt-1">
                  {col.meta}
                </p>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Fullscreen Showreel Modal */}
      <AnimatePresence>
        {showreelModalOpen && (
          <motion.div
            className="fixed inset-0 z-[130] bg-black/95 backdrop-blur-2xl flex items-center justify-center p-3 sm:p-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowreelModalOpen(false)}
          >
            <div
              className="relative w-full max-w-6xl aspect-video bg-black rounded-xl overflow-hidden border border-white/15"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                onClick={() => setShowreelModalOpen(false)}
                className="absolute top-3 right-3 sm:top-4 sm:right-4 z-20 w-9 h-9 rounded-full bg-[#E50914] text-white flex items-center justify-center hover:bg-white hover:text-black transition-colors cursor-pointer"
                aria-label="Close showreel"
              >
                ✕
              </button>
              <video
                src={activeVideoSrc}
                poster={activePosterSrc}
                controls
                autoPlay
                playsInline
                className="w-full h-full object-contain bg-black"
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default HeroSection;
